/**
 * KbFiles — вкладки «Документы» и «Таблицы» в базе знаний.
 *
 * Что это на самом деле: список ПЕРВОИСТОЧНИКОВ, прикреплённых к базе знаний.
 * Бот не «открывает» файлы — при загрузке сервер извлекает из них текст и
 * вкладывает его в раздел БЗ, а сам файл остаётся здесь как исходник, чтобы
 * было видно, откуда взялись цифры, и можно было заменить его новой версией.
 *
 * Поэтому удаление файла НЕ стирает текст из раздела: бот отвечает по тексту,
 * и молча выкидывать содержимое из-за удаления вложения было бы неожиданно.
 */

import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, FileSpreadsheet, FileText, Link2, Loader2, Paperclip, RefreshCw, Trash2, Unlink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { kbApi } from '@/api/client'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

type Kind = 'table' | 'document'

const COPY: Record<Kind, { hint: string; accept: string; empty: string }> = {
  table: {
    hint: 'Прайс-листы, каталоги, номенклатура. Из таблицы извлекаются строки и попадают в раздел базы знаний — по ним бот и отвечает.',
    accept: '.xlsx,.xlsm,.csv',
    empty: 'Таблиц пока нет. Прикрепите прайс или каталог.',
  },
  document: {
    hint: 'Договоры, регламенты, инструкции. Из документа извлекается текст и попадает в раздел базы знаний.',
    accept: '.docx,.pdf,.txt,.md',
    empty: 'Документов пока нет. Прикрепите договор или регламент.',
  },
}

function fmtSize(b: number): string {
  if (b < 1024) return `${b} Б`
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} КБ`
  return `${(b / 1024 / 1024).toFixed(1)} МБ`
}

export function KbFiles({ dealId, kind }: { dealId: string; kind: Kind }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [showConnect, setShowConnect] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['kb-files', dealId],
    // Тип берём от самой ручки: без него пустой массив мока сводил элемент к
    // `never`, и `f.kind` ниже переставал существовать для проверки типов.
    queryFn: async (): Promise<Awaited<ReturnType<typeof kbApi.files>>> =>
      USE_MOCK ? [] : kbApi.files(dealId),
    staleTime: 30 * 1000,
  })

  const { data: sources } = useQuery({
    queryKey: ['kb-google', dealId],
    queryFn: async () => (USE_MOCK ? [] : kbApi.googleList(dealId)),
    staleTime: 30 * 1000,
  })

  // На вкладке «Таблицы» показываем подключённые таблицы, на «Документах» — доки.
  const wantKind = kind === 'table' ? 'gsheet' : 'gdoc'
  const docs = (sources ?? []).filter((d) => d.kind === wantKind)

  async function connect() {
    const u = url.trim()
    if (!u) return
    setBusy('connect')
    setError(null)
    setNote(null)
    try {
      const res = await kbApi.googleConnect(dealId, u)
      await qc.invalidateQueries({ queryKey: ['kb-google', dealId] })
      await qc.invalidateQueries({ queryKey: ['kb', dealId] })
      setUrl('')
      setShowConnect(false)
      setNote(`«${res.title}» подключён: ${res.extracted_chars.toLocaleString('ru-RU')} знаков в разделе.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось подключить документ.')
    } finally {
      setBusy(null)
    }
  }

  async function sync(id: string) {
    setBusy(id)
    setError(null)
    setNote(null)
    try {
      const res = await kbApi.googleSync(dealId, id)
      await qc.invalidateQueries({ queryKey: ['kb-google', dealId] })
      await qc.invalidateQueries({ queryKey: ['kb', dealId] })
      setNote(`«${res.title}» перечитан: ${res.extracted_chars.toLocaleString('ru-RU')} знаков.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось обновить документ.')
    } finally {
      setBusy(null)
    }
  }

  async function disconnect(id: string) {
    setBusy(id)
    setError(null)
    try {
      await kbApi.googleDisconnect(dealId, id)
      await qc.invalidateQueries({ queryKey: ['kb-google', dealId] })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отвязать документ.')
    } finally {
      setBusy(null)
    }
  }

  const files = (data ?? []).filter((f) => f.kind === kind)
  const copy = COPY[kind]
  const Icon = kind === 'table' ? FileSpreadsheet : FileText

  async function remove(filename: string) {
    setBusy(filename)
    setError(null)
    try {
      if (!USE_MOCK) await kbApi.removeFile(dealId, filename)
      await qc.invalidateQueries({ queryKey: ['kb-files', dealId] })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось удалить файл.')
    } finally {
      setBusy(null)
    }
  }

  async function attach(file: File) {
    setBusy('upload')
    setError(null)
    setNote(null)
    try {
      if (USE_MOCK) {
        setNote('Демо-режим: файл не загружается.')
        return
      }
      const res = await kbApi.attachFile(dealId, file)
      await qc.invalidateQueries({ queryKey: ['kb-files', dealId] })
      await qc.invalidateQueries({ queryKey: ['kb', dealId] })
      const parts = [
        `Текст добавлен в раздел «${res.title}»: ${res.extracted_chars.toLocaleString('ru-RU')} знаков.`,
      ]
      if (res.truncated) parts.push('Текст обрезан по лимиту — проверьте, что осталось главное.')
      if (res.note) parts.push(res.note)
      setNote(parts.join(' '))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось прикрепить файл.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground leading-relaxed">{copy.hint}</p>

      <div className="relative flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          className="gap-1.5 text-xs h-8"
          disabled={busy !== null}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {busy === 'upload' ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="text-sm leading-none">+</span>}
          Подключить {kind === 'table' ? 'таблицу' : 'документ'}
          <ChevronDown className="h-3 w-3" />
        </Button>

        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div className="absolute left-0 top-9 z-20 w-72 rounded-lg border bg-background p-1.5 shadow-lg">
              <button
                type="button"
                className="flex w-full items-start gap-3 rounded px-2 py-2 text-left hover:bg-accent"
                onClick={() => {
                  setMenuOpen(false)
                  inputRef.current?.click()
                }}
              >
                <Paperclip className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  <span className="block text-sm">Импортировать файл</span>
                  <span className="block text-[11px] text-muted-foreground">
                    Файл с вашего компьютера
                  </span>
                </span>
              </button>

              <button
                type="button"
                className="flex w-full items-start gap-3 rounded px-2 py-2 text-left hover:bg-accent"
                onClick={() => {
                  setMenuOpen(false)
                  setShowConnect(true)
                }}
              >
                <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  <span className="block text-sm">
                    Подключиться к Google-{kind === 'table' ? 'таблице' : 'документу'}
                  </span>
                  <span className="block text-[11px] text-muted-foreground">
                    По ссылке с открытым доступом
                  </span>
                </span>
              </button>
            </div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept={copy.accept}
          onChange={(e) => {
            const f = e.target.files?.[0]
            e.target.value = ''
            if (f) attach(f)
          }}
        />
      </div>

      {showConnect && (
        <div className="space-y-2 rounded-md border bg-slate-50/60 p-3">
          <input
            autoFocus
            className="w-full rounded border bg-background px-2 py-1.5 text-sm"
            placeholder="https://docs.google.com/…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') connect()
            }}
          />
          <p className="text-[11px] text-muted-foreground">
            Документ должен быть открыт «Всем, у кого есть ссылка — Читатель».
            Приватные Google так не отдаёт.
          </p>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs" disabled={busy === 'connect' || !url.trim()} onClick={connect}>
              {busy === 'connect' && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Подключить
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowConnect(false)}>
              Отмена
            </Button>
          </div>
        </div>
      )}

      {docs.length > 0 && (
        <div className="divide-y rounded-md border">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-3 py-2">
              <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <a href={d.url} target="_blank" rel="noreferrer" className="truncate text-sm hover:underline block">
                  {d.title}
                </a>
                <p className={`text-[11px] ${d.last_error ? 'text-amber-700' : 'text-muted-foreground'}`}>
                  {d.last_error
                    ? d.last_error
                    : d.last_synced_at
                      ? `обновлён ${new Date(d.last_synced_at).toLocaleString('ru-RU')}`
                      : 'ещё не читался'}
                </p>
              </div>
              <button
                type="button"
                disabled={busy === d.id}
                onClick={() => sync(d.id)}
                title="Перечитать документ"
                className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
              >
                {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                disabled={busy === d.id}
                onClick={() => disconnect(d.id)}
                title="Отвязать документ"
                className="rounded p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
              >
                <Unlink className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {note && (
        <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
          {note}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Загружаю…
        </div>
      ) : files.length === 0 ? (
        <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
          {copy.empty}
        </p>
      ) : (
        <div className="divide-y rounded-md border">
          {files.map((f) => (
            <div key={f.filename} className="flex items-center gap-3 px-3 py-2">
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{f.filename}</p>
                <p className="text-[11px] text-muted-foreground">
                  {fmtSize(f.size_bytes)} · {new Date(f.uploaded_at * 1000).toLocaleString('ru-RU')}
                </p>
              </div>
              <button
                type="button"
                disabled={busy === f.filename}
                onClick={() => remove(f.filename)}
                title="Удалить файл"
                aria-label={`Удалить: ${f.filename}`}
                className="rounded p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
              >
                {busy === f.filename ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        Раздел создаётся из имени файла автоматически — выбирать ничего не нужно.
        Удаление убирает только сам файл: текст, извлечённый при загрузке, остаётся
        в разделе, бот отвечает по нему. Правится он на вкладке «Разделы».
      </p>
    </div>
  )
}
